import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner, Form } from 'react-bootstrap';
import { AnimatePresence, motion } from 'framer-motion';
import { extractErrorMessage } from '../../utils/httpErrorEnvelope';

/**
 * Re-auth prompt for the backend's `step_up_required`.
 *
 * The backend refuses to move money on a session that authenticated too long
 * ago (FreshAuthGuard). This sends a fresh code to the phone already on the
 * account, takes the code, and swaps in the re-issued token. The API layer
 * then retries whatever was blocked, so the user lands back where they were
 * rather than on a dead error.
 *
 * The number is never entered here and never comes down from the server in
 * full: the backend reads it from the account and returns only a masked hint.
 */
const StepUpModal = ({ show, onVerified, onCancel, api }) => {
  const [phase, setPhase] = useState('sending');
  const [phoneHint, setPhoneHint] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const sendCode = useCallback(async () => {
    setError('');
    setPhase('sending');
    try {
      const res = await api.post('/auth/step-up/send', {});
      setPhoneHint(res?.phoneHint || '');
      setPhase('entering');
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          'We could not send a verification code. Please try again.',
        ),
      );
      setPhase('error');
    }
  }, [api]);

  useEffect(() => {
    if (show) {
      setCode('');
      setError('');
      sendCode();
    }
  }, [show, sendCode]);

  useEffect(() => {
    if (phase === 'entering') inputRef.current?.focus();
  }, [phase]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post('/auth/step-up/verify', { code: code.trim() });
      if (!res?.token) throw new Error('Verification failed. Please try again.');
      // Swap the stored token BEFORE resolving, so the retried request picks
      // up the fresh auth_time.
      localStorage.setItem('authToken', res.token);
      onVerified();
    } catch (err) {
      setError(
        extractErrorMessage(err, 'That code was not valid. Please try again.'),
      );
      setCode('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
          style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
          role='dialog'
          aria-modal='true'
          aria-label='Confirm it is you'
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className='bg-white rounded-4 shadow p-4'
            style={{ maxWidth: '420px', width: '100%' }}
          >
            <h5 className='fw-bold mb-2'>Confirm it's you</h5>
            <p className='text-muted mb-3'>
              For your security we ask you to confirm your identity before
              changing billing.
            </p>

            {phase === 'sending' && (
              <div className='d-flex align-items-center gap-2 py-2'>
                <Spinner animation='border' size='sm' role='status' />
                <span>Sending a verification code...</span>
              </div>
            )}

            {phase === 'error' && (
              <>
                <p className='text-danger'>{error}</p>
                <div className='d-flex justify-content-end gap-2 mt-3'>
                  <Button variant='outline-secondary' onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button variant='primary' onClick={sendCode}>
                    Try again
                  </Button>
                </div>
              </>
            )}

            {phase === 'entering' && (
              <Form onSubmit={submit}>
                <Form.Label htmlFor='step-up-code'>
                  Enter the code we sent to{' '}
                  <strong>{phoneHint || 'your phone on file'}</strong>
                </Form.Label>
                <Form.Control
                  id='step-up-code'
                  ref={inputRef}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode='numeric'
                  autoComplete='one-time-code'
                  placeholder='123456'
                  disabled={busy}
                />
                {error && (
                  <div className='text-danger mt-2' role='alert'>
                    {error}
                  </div>
                )}
                <div className='d-flex justify-content-between align-items-center mt-4'>
                  <Button
                    variant='link'
                    className='px-0'
                    onClick={sendCode}
                    disabled={busy}
                  >
                    Resend code
                  </Button>
                  <div className='d-flex gap-2'>
                    <Button
                      variant='outline-secondary'
                      onClick={onCancel}
                      disabled={busy}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      variant='primary'
                      disabled={busy || !code.trim()}
                    >
                      {busy ? (
                        <>
                          <Spinner
                            as='span'
                            animation='border'
                            size='sm'
                            role='status'
                            aria-hidden='true'
                            className='me-2'
                          />
                          Verifying...
                        </>
                      ) : (
                        'Confirm'
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StepUpModal;
